import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emptyLine, parseLines, type LineDraft } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot, PageHeader } from '../ui/Page';
import { SectionCard, useHashAnchor } from '../ui/Detail';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { money } from '../money';
import { can } from '@servwell/shared';
import { useSession } from '../session';
import { Field, field } from '../create/Field';
import { LineItemBuilder } from '../money/LineItemBuilder';
import { ChecklistsCard } from '../checklists/ChecklistsCard';
import { LocationsCard } from '../inventory/LocationsCard';
import { IconTeam, IconQuotes, IconTrash } from '../icons';
import { CustomFieldsSettingsCard } from '../customfields/CustomFieldsSettingsCard';
import { ServiceTypesSettingsCard } from '../servicetypes/ServiceTypesSettingsCard';
import { JobPipelineSettingsCard } from '../settings/JobPipelineSettingsCard';
import { JobLayoutSettingsCard } from '../settings/JobLayoutSettingsCard';
import { CrewAvailabilitySettingsCard } from '../settings/CrewAvailabilitySettingsCard';
import { GettingPaidCard } from '../collections/GettingPaidCard';
import { AutomationsCard } from '../settings/AutomationsCard';
import { ConnectedAiCard, ShopDetailsCard } from '../settings/ConnectedAiCard';
import { EmailCard } from '../settings/EmailCard';
import { ConnectedServicesCard } from '../settings/ConnectedServicesCard';
import { PhotoStorageCard } from '../settings/PhotoStorageCard';
import { YourDataCard } from '../settings/YourDataCard';
import { TeamCard } from '../settings/TeamCard';
import { MonitorsCard } from '../settings/MonitorsCard';
import { MorningEmailSwitch } from '../settings/MorningEmailSwitch';
import { Link } from '@tanstack/react-router';

/**
 * Settings, in the order an owner needs them: who they are, what the team costs (the thing
 * that makes margin real), what happens to unpaid invoices, then the things they set up
 * once and forget. Connected AI and the honest-terms surfaces sit at the bottom — the last
 * two are deliberately NOT buried in a submenu, because "you can export and you can leave"
 * only counts if you can find it.
 */
/**
 * Each card below carries the anchor that addresses it — `/settings#hourly-cost`.
 *
 * They are named after the visible heading, so a person can guess one, and they are passed
 * DOWN rather than hardcoded inside each card: several of these cards are reusable, and two
 * elements sharing a DOM id resolve to whichever rendered first, which is a link that works
 * and lands on the wrong card. The composer owns the namespace.
 *
 * An anchor names the CARD, not its position — the Wave 3 regrouping may move these anywhere
 * it likes. It may not rename them: a URL that has been pasted into an email is a promise.
 * A card that leaves for a page of its own keeps its anchor there, and the old URL follows it
 * (`movedFromSettings.ts`; the price book, CRM-18).
 * `settings-anchors.test.ts` harvests them from this JSX and fails on a card with no anchor,
 * a duplicate, or a promised name that has gone missing.
 */
export function SettingsPage() {
  // The router lands the deep link; this holds it there while the cards above finish
  // loading, on the browsers that do not do it themselves. See `useHashAnchor`.
  useHashAnchor();
  return (
    <PageRoot>
      <PageHeader
        title="Settings"
        sub="What your team costs, so every job can tell you whether it made money."
      />

      <YourAccountCard id="you" />
      <ShopDetailsCard id="your-shop" />
      <TeamCard id="your-team" />

      <HourlyCostCard id="hourly-cost" />

      <GettingPaidCard id="getting-paid" />

      <AutomationsCard id="automations" />

      {/* MAP 249 ticket 07: how the shop sends, today's count, the reply address, the
          automatic emails, the review link and who asked not to hear (owner edits, office
          reads). Every email banner lands on a control in here. */}
      <EmailCard id="email" />

      {/* The price book moved to its own page, Products & services (CRM-18). The old
          `/settings#price-book` link lands on it there (`movedFromSettings.ts`). */}

      <TemplatesCard id="templates" />

      <ServiceTypesSettingsCard id="service-types" />

      <JobPipelineSettingsCard id="job-pipelines" />
      <JobLayoutSettingsCard id="job-workspace-cards" />
      <CrewAvailabilitySettingsCard id="crew-availability" />

      <CustomFieldsSettingsCard id="custom-fields" />

      <ChecklistsCard id="checklists" />

      <LocationsCard id="stock-locations" />

      <SectionCard id="bringing-data-over" title="Bringing data over">
        <p className="text-sm text-muted-foreground">
          Moving from another tool? Export a CSV from it and bring your customers or price
          book across — you'll see exactly what you're getting before anything saves.
        </p>
        <Link
          to="/import"
          className="mt-2 inline-flex min-h-9 items-center text-sm font-semibold text-[var(--sw-brand-text)] hover:underline"
        >
          Open the importer
        </Link>
      </SectionCard>

      <ConnectedServicesCard id="connected-services" />

      <MonitorsCard id="monitors" />

      <ConnectedAiCard id="connect-your-ai" />

      <PhotoStorageCard id="photo-storage" />

      <YourDataCard id="your-data" />
    </PageRoot>
  );
}

function RateRow({
  name,
  role,
  hourlyCostCents,
  pending,
  readOnly,
  onSave,
}: {
  name: string;
  role: string;
  hourlyCostCents: number | null;
  pending: boolean;
  /** The viewer may read rates but not set them — show the figure, offer no edit. */
  readOnly: boolean;
  onSave: (cents: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  // Losing permission mid-edit closes the row rather than parking a draft that reappears if
  // the role comes back. `editing && !readOnly` below already removes the save path; this
  // stops a half-typed rate resurfacing later with no memory of where it came from.
  useEffect(() => {
    if (readOnly) setEditing(false);
  }, [readOnly]);
  const [value, setValue] = useState(
    hourlyCostCents === null ? '' : (hourlyCostCents / 100).toFixed(2),
  );

  return (
    <li className="flex flex-wrap items-center gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name}</p>
        <p className="text-xs text-muted-foreground">{role}</p>
      </div>

      {/* `!readOnly` as well as `editing`: a viewer who loses `member:update` while a row is
          already open would otherwise keep a live Save. Self-demotion is reachable — the
          team card writes roles and invalidates the session. */}
      {editing && !readOnly ? (
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = value.trim();
            onSave(trimmed === '' ? null : Math.round(Number(trimmed) * 100));
            setEditing(false);
          }}
        >
          <label className="flex items-center gap-1 text-sm">
            <span className="text-muted-foreground">$</span>
            <input
              className="w-24 rounded-[var(--radius-sm)] border border-border bg-surface px-2 py-1 text-sm tabular-nums"
              type="number"
              min="0"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label={`Hourly cost for ${name}`}
              autoFocus
            />
            <span className="text-xs text-muted-foreground">/hr</span>
          </label>
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </form>
      ) : (
        <div className="flex items-center gap-3">
          {hourlyCostCents === null ? (
            <StatusBadge tone="warn">Not set</StatusBadge>
          ) : (
            <span className="text-sm font-medium tabular-nums">
              {money(hourlyCostCents)}
              <span className="text-xs font-normal text-muted-foreground"> /hr</span>
            </span>
          )}
          {readOnly ? null : (
            <Button variant="ghost" onClick={() => setEditing(true)}>
              {hourlyCostCents === null ? 'Set cost' : 'Change'}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Who you are signed in as. Small, and load-bearing in exactly one situation: a shop where
 * the owner and the office manager share a laptop, and somebody is about to void an invoice
 * under the wrong name. The role is shown because it explains why a button is missing.
 */
function YourAccountCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const me = useQuery(trpc.me.whoami.queryOptions());
  /**
   * The shell renders BEFORE the data does, or `#you` does not exist at the one moment the
   * router looks for it. `getElementById` runs once, in the layout effect of the first commit
   * after this subtree mounts, and never retries — and `whoami` cannot have resolved by then
   * on a cold cache, because it does not start until this card mounts. Returning `null` while
   * pending made the first anchor on the page a silent no-op on every first-ever arrival.
   *
   * The error branch keeps the shell for the same reason. 018 wrote the contract it now
   * asks — `UnresolvedState`, which is the whole settings family's one answer to a query that did
   * not come back. What it must never do is go back to replacing the whole card, because
   * the anchor lives on the shell and would go with it.
   */
  const meState = shownState(me, me.data);
  if (!me.data) {
    return (
      <SectionCard id={id} title="You">
        <UnresolvedState
          state={unresolved(meState)}
          what="your account"
          icon={IconTeam}
          error={me.error}
          onRetry={() => void me.refetch()}
          skeleton="h-16"
        />
      </SectionCard>
    );
  }
  return (
    <SectionCard id={id} title="You">
      <StaleNotice show={refreshFailed(me, me.data)} onRetry={() => void me.refetch()} />
      <dl className="space-y-2 text-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Signed in as</dt>
          <dd className="font-medium">{me.data.name ?? me.data.email}</dd>
        </div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Email</dt>
          <dd>{me.data.email}</dd>
        </div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Your role here</dt>
          <dd>
            <StatusBadge tone="neutral">{me.data.role ?? 'unknown'}</StatusBadge>
          </dd>
        </div>
      </dl>
      <p className="mt-2 text-xs text-muted-foreground">
        Your role decides what you can see and change. Only an owner can change somebody's
        role.
      </p>
      {/* The person's own morning email; it draws nothing for a role that never gets one. */}
      <MorningEmailSwitch />
    </SectionCard>
  );
}

/** Templates a shop has saved — reviewable and removable in one place. */
function TemplatesCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const templates = useQuery(trpc.template.list.queryOptions());
  const [writing, setWriting] = useState(false);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const remove = useMutation(
    trpc.template.delete.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );
  const create = useMutation(
    trpc.template.create.mutationOptions({
      onSuccess: () => {
        setWriting(false);
        setName('');
        setTitle('');
        setDescription('');
        setLines([emptyLine()]);
        void qc.invalidateQueries();
      },
    }),
  );

  const templatesState = shownState(templates, templates.data);

  return (
    <SectionCard
      id={id}
      title={counted('Templates', templatesState, templates.data?.length ?? 0)}
      right={
        writing || templatesState !== 'ready' ? null : (
          <Button onClick={() => setWriting(true)}>New template</Button>
        )
      }
    >
      {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}
      {writing ? (
        <div className="mb-3 space-y-2 rounded-[var(--radius)] border border-border p-3">
          {/* Writing one from scratch, for the work a shop KNOWS it sells before it has
              ever quoted it — "Save as template" only captures what already happened. */}
          <Field label="Call it">
            <input
              className={field}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Annual furnace tune-up"
              autoFocus
            />
          </Field>
          <Field label="What the customer sees">
            <input
              className={field}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Furnace maintenance — annual service"
            />
          </Field>
          <Field label="Details">
            <textarea
              className="min-h-[60px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Inspect heat exchanger, replace filter, test safeties, clean burners."
            />
          </Field>
          <LineItemBuilder lines={lines} onChange={setLines} />
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={!name.trim() || !title.trim() || create.isPending}
              onClick={() =>
                create.mutate({
                  name: name.trim(),
                  title: title.trim(),
                  description: description.trim() || undefined,
                  lines: parseLines(lines),
                  ...idem(),
                })
              }
            >
              {create.isPending ? 'Saving…' : 'Save template'}
            </Button>
            <Button variant="ghost" onClick={() => setWriting(false)}>
              Never mind
            </Button>
          </div>
        </div>
      ) : null}
      {!templates.data ? (
        <UnresolvedState
          state={unresolved(templatesState)}
          what="your templates"
          icon={IconQuotes}
          error={templates.error}
          onRetry={() => void templates.refetch()}
        />
      ) : (
        <>
          <StaleNotice
            show={refreshFailed(templates, templates.data)}
            onRetry={() => void templates.refetch()}
          />
          {templates.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              None yet. Write a quote you'd send again, then use{' '}
              <strong>Save as template</strong> on it — that's where templates come from.
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {templates.data.map((t) => (
                <li key={t.id} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{t.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {t.lines.length} line{t.lines.length === 1 ? '' : 's'} ·{' '}
                      {t.appliesTo}
                    </p>
                  </div>
                  <span className="font-medium tabular-nums">
                    {money(t.lines.reduce((sum, l) => sum + l.totalCents, 0))}
                  </span>
                  <button
                    type="button"
                    aria-label={`Delete template ${t.name}`}
                    onClick={() => remove.mutate({ templateId: t.id, ...idem() })}
                    className="rounded-[var(--radius-sm)] p-1 text-muted-foreground hover:bg-surface-2 hover:text-[var(--sw-danger-text)]"
                  >
                    <IconTrash size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </SectionCard>
  );
}

/**
 * What the shop pays its people, which is what makes margin real.
 *
 * ⚠️ **This owns its own query on purpose.** It used to live on the page, gated by two early
 * returns — a full-page skeleton while `costing.memberRates` loaded, and a full-page error
 * if it failed. Fifteen cards, including the shop's name, its price book, its checklists and
 * its export button, hung on whether one question about hourly rates came back. A role
 * without `member:read` got the whole page collapsed into "Couldn't load your team", under a
 * Retry that could never succeed.
 *
 * A card that needs data is the only place that can be honest about that data being missing.
 */
function HourlyCostCard({ id }: { id: string }) {
  const trpc = useTRPC();
  // `setMemberRate` needs `member:update`, which `office` does NOT hold — it has read and
  // invite only. The route admits office to this page, so without this the card would offer
  // an editable rate that fails on save with nothing on screen to explain it: a control that
  // cannot change, which is the defect class this wave exists to remove.
  const canEditRates = can(useSession().role, 'member', 'update');
  const qc = useQueryClient();
  const rates = useQuery(trpc.costing.memberRates.queryOptions());
  const setRate = useMutation(
    trpc.costing.setMemberRate.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  const state = shownState(rates, rates.data);
  const team = rates.data ?? [];
  const unrated = team.filter((m) => m.hourlyCostCents === null).length;

  return (
    // No count until there IS one: `(0)` while loading is a number the page does not know
    // yet, and it reads as "nobody works here".
    <SectionCard
      id={id}
      title={
        state === 'ready'
          ? `Hourly cost by person (${team.length})`
          : 'Hourly cost by person'
      }
    >
      {/*
        This card WROTE the contract — the four-way branch, the forbidden dispatch and the
        suppressed count were all here first, for one card. `UnresolvedState` is this block lifted
        out, so it now consumes what it inspired and the other fourteen cards say the same
        words it did.
      */}
      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          what="your hourly costs"
          icon={IconTeam}
          error={rates.error}
          onRetry={() => void rates.refetch()}
        />
      ) : (
        <>
          <StaleNotice
            show={refreshFailed(rates, rates.data)}
            onRetry={() => void rates.refetch()}
          />
          {unrated > 0 ? (
            <div className="mb-3">
              <Notice tone="warn">
                {unrated === 1 ? 'One person has' : `${unrated} people have`} no hourly
                cost set. Their hours still get logged, but jobs they work will report an
                optimistic margin until you fill this in.
              </Notice>
            </div>
          ) : null}

          <p className="mb-3 text-xs text-muted-foreground">
            Use the <strong>burdened</strong> cost — wage plus payroll tax, insurance and
            vehicle. Changing a rate only affects work logged from now on; finished jobs
            keep the rate they were costed at.
          </p>

          <ul className="divide-y divide-border">
            {team.map((m) => (
              <RateRow
                key={m.userId}
                name={m.name ?? m.email}
                role={m.role}
                hourlyCostCents={m.hourlyCostCents}
                readOnly={!canEditRates}
                pending={setRate.isPending && setRate.variables?.userId === m.userId}
                onSave={(cents) =>
                  setRate.mutate({ userId: m.userId, hourlyCostCents: cents, ...idem() })
                }
              />
            ))}
          </ul>
        </>
      )}
    </SectionCard>
  );
}
